import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import type { CartLineT } from '@lambda/apis/src/cart/types';
import type { DeliveryEstimateT } from '@lambda/apis/src/catalog-v2/types';
import { priceFormater } from '@/settings';
import { priceFormatSuperscript } from '@/lib/price';
import i18n from '@/settings/i18n';
import { formatDeliveryDates } from '../helpers';

const t = i18n.init('cart');

const TOOLTIP_ID = 'cart-seller-breakdown';

const deliveryFor = (estimates: DeliveryEstimateT[] | undefined) => {
  const standard = estimates?.find((estimate) => estimate.type === 'standard');

  if (!standard) {
    return '';
  }

  return formatDeliveryDates({
    minDeliveryDate: standard.minDate,
    maxDeliveryDate: standard.maxDate,
  });
};

/**
 * The note that a SKU is coming from more than one seller, with the split
 * behind it: how many units each is covering, at what unit price, arriving
 * when. Opens on hover and on focus, so it is reachable by keyboard too.
 */
const SellerBreakdown = ({
  lines,
  deliveryEstimates,
}: {
  lines: CartLineT[];
  deliveryEstimates: Record<string, DeliveryEstimateT[]>;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Tapping outside dismisses it on touch, where there is no hover to lose.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('touchstart', onPointerDown);

    return () => document.removeEventListener('touchstart', onPointerDown);
  }, [open]);

  return (
    <div
      className="relative w-fit"
      ref={containerRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="flex items-center gap-1 text-left text-xs text-gray-700/60"
        aria-expanded={open}
        aria-controls={TOOLTIP_ID}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {t`Products sent separately from multiple verified sellers.`}
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={TOOLTIP_ID}
          role="tooltip"
          className="absolute left-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-xl border bg-white text-xs text-gray-700 shadow-[0_8px_24px_0_rgba(0,0,0,0.08)]"
        >
          <ul className="divide-y text-xs">
            {lines.map((line) => (
              <li
                key={line.id}
                className="flex flex-wrap items-center justify-between gap-1 px-4 py-3"
              >
                <span className="text-gray-700/60">
                  {t`${{ quantity: line.quantity }} for`}{' '}
                  <span className="font-medium text-gray-700" translate="no">
                    {priceFormatSuperscript(
                      priceFormater.short(line.price / 100),
                    )}
                  </span>{' '}
                  {t`per unit`}
                </span>
                <span className="text-gray-700/60">
                  {t`FREE Delivery`} {deliveryFor(deliveryEstimates[line.id])}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SellerBreakdown;
